import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../ApiService';
import { preCacheRoute } from '../cache/routePreCache';
import { trackEvent } from '../analytics';

export default function useRoute({ user }) {
  const { t, i18n } = useTranslation();
  const [selectedList, setSelectedList] = useState(new Map());
  const [route, setRoute] = useState(false);
  const [routeMode, setRouteMode] = useState(false);
  const [customRouteOrigin, setCustomRouteOrigin] = useState(null);
  const [customRouteDest, setCustomRouteDest] = useState(null);
  const [cacheProgress, setCacheProgress] = useState({ active: false, done: 0, total: 0 });

  const createRoute = useCallback(async (intermediates, origin = null, destination = null) => {
    try {
      const intermediateKeys   = intermediates ? Array.from(intermediates.keys())   : [];
      const intermediateValues = intermediates ? Array.from(intermediates.values()) : [];

      let routeOrigin      = origin;
      let routeDestination = destination;
      let routeIntermediates = intermediateKeys;

      if (!routeOrigin && !routeDestination) {
        if (intermediateValues.length < 2) {
          setRoute(null);
          return;
        }
        const first = intermediateValues[0];
        const last  = intermediateValues[intermediateValues.length - 1];

        routeOrigin      = { lat: first.location.latitude, lng: first.location.longitude };
        routeDestination = { lat: last.location.latitude,  lng: last.location.longitude  };

        // First and last are now origin/destination — remove them from stops
        routeIntermediates = intermediateKeys.slice(1, intermediateKeys.length - 1);
      }

      if (!routeOrigin) return;

      const routeData = await ApiService.post('/route/create/', {
        origin: {
          longitude: routeOrigin.lng,
          latitude:  routeOrigin.lat,
        },
        destination: {
          longitude: routeDestination.lng,
          latitude:  routeDestination.lat,
        },
        intermediates: routeIntermediates,
        language: i18n.language,
        travel_mode: 'WALK',
        optimize_waypoint_order: true,
      }, user);

      if (routeData) {
        trackEvent('route_created', { place_count: intermediates?.size ?? 0 });
        const optimized = routeData.optimized_intermediate_waypoint_index;

        if (optimized && Array.isArray(optimized) && optimized.length > 1 && optimized.length === routeIntermediates.length) {
          const reorderedIntermediates = optimized.map(i => routeIntermediates[i]);

          let finalOrderedKeys;
          if (!origin && !destination) {
            finalOrderedKeys = [
              intermediateKeys[0],
              ...reorderedIntermediates,
              intermediateKeys[intermediateKeys.length - 1],
            ];
          } else {
            finalOrderedKeys = reorderedIntermediates;
          }

          const reorderedMap = new Map(finalOrderedKeys.map(key => [key, intermediates.get(key)]));
          setSelectedList(reorderedMap);
        }

        setRoute(routeData);
        return routeData;
      } else {
        console.error('Failed to fetch route');
      }
    } catch (error) {
      console.error('Error fetching route:', error);
    }
  }, [user, i18n]);

  const handleSelectIconClick = async (e, place, currentRouteMode) => {
    e.stopPropagation();

    if (currentRouteMode) return;

    const current_selection = new Map(selectedList);
    const sPlace = current_selection.get(place.reference);
    if (!sPlace) {
      current_selection.set(place.reference, place);
      trackEvent('place_added_to_route', { place_name: place.description?.name, route_size: current_selection.size });
    } else {
      current_selection.delete(place.reference);
      trackEvent('place_removed_from_route', { place_name: place.description?.name });
    }

    setSelectedList(current_selection);

    const hasCustomEndpoints = customRouteOrigin || customRouteDest;
    if ((hasCustomEndpoints && current_selection.size > 0) || current_selection.size >= 2) {
      await createRoute(current_selection, customRouteOrigin, customRouteDest);
    } else {
      setRoute(null);
    }
  };

  const handleRouteMode = () => {
    const newRouteMode = !routeMode;
    if (newRouteMode) {
      if (!route) {
        alert(t('route.alert_no_route_created'));
        return;
      }
      trackEvent('navigation_started', { place_count: selectedList.size });
      setRouteMode(newRouteMode);
    } else {
      setRouteMode(newRouteMode);
    }
  };

  const getPlacesForDisplay = (places) => {
    if (!routeMode) {
      return Array.from(places.values());
    }
    return Array.from(selectedList.values());
  };

  const saveRouteOffline = useCallback(async (name) => {
    const places = Array.from(selectedList.values());
    if (!route || places.length === 0) {
      throw new Error('no_route_to_save');
    }
    setCacheProgress({ active: true, done: 0, total: places.length * 3 });
    try {
      await preCacheRoute({
        id: Date.now().toString(),
        name,
        route,
        places,
        user,
        language: i18n.language,
        originLabel: customRouteOrigin?.label ?? null,
        destLabel: customRouteDest?.label ?? null,
        onProgress: (done, total) => {
          if (done >= total) {
            setCacheProgress({ active: false, done, total });
          } else {
            setCacheProgress({ active: true, done, total });
          }
        },
      });
    } catch (err) {
      setCacheProgress(prev => ({ ...prev, active: false }));
      throw err;
    }
  }, [route, selectedList, customRouteOrigin, customRouteDest, user, i18n]);

  const onSelectItinerary = async (e, itinerary, places, fetchExtraPlace) => {
    e.stopPropagation();
    const current_selection = new Map();

    for (let index = 0; index < itinerary.waypoints.length; index++) {
      const place_ref = itinerary.waypoints[index];
      if (places.has(place_ref)) {
        const place = places.get(place_ref);
        current_selection.set(place.reference, place);
      } else {
        const place = await fetchExtraPlace(place_ref);
        if (place) current_selection.set(place.reference, place);
      }
    }

    setSelectedList(current_selection);
    await createRoute(current_selection);
    trackEvent('navigation_started', { place_count: current_selection.size });
    setRouteMode(true);
  };

  return {
    selectedList,
    setSelectedList,
    route,
    setRoute,
    routeMode,
    setRouteMode,
    customRouteOrigin,
    setCustomRouteOrigin,
    customRouteDest,
    setCustomRouteDest,
    createRoute,
    handleSelectIconClick,
    handleRouteMode,
    getPlacesForDisplay,
    onSelectItinerary,
    saveRouteOffline,
    cacheProgress,
  };
}
